import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { guideSections } from './guideData';

const TOUR_STORAGE_KEY = 'clinique_medecin_tour_count';

export default function GuideMedecinPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<string>(guideSections[0]?.id || '');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (role !== 'medecin' && role !== 'super_admin') {
      navigate('/', { replace: true });
    }
  }, [user, role, authLoading, navigate]);

  const replayTour = () => {
    try {
      localStorage.removeItem(TOUR_STORAGE_KEY);
    } catch {
      // silencieux
    }
    navigate('/medecin/rendez-vous', { replace: true });
  };

  if (authLoading || !user || (role !== 'medecin' && role !== 'super_admin')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin"></i>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* ─── Hero ───────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-clinic-blue-dark via-clinic-blue to-clinic-green">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                <i className="ri-book-open-line"></i>
                Centre d'aide médecin
              </div>
              <h1 className="text-2xl md:text-4xl font-bold mb-3">Guide du médecin</h1>
              <p className="text-white/90 text-sm md:text-base max-w-2xl">
                Apprenez à utiliser chaque fonctionnalité de la clinique, pas à pas.
                Suivez les tutoriels ci-dessous ou relancez la visite guidée interactive.
              </p>
            </div>
            <button
              onClick={replayTour}
              className="shrink-0 px-5 md:px-6 py-3 bg-white text-clinic-blue-dark rounded-full font-semibold text-sm md:text-base whitespace-nowrap cursor-pointer transition-all hover:bg-white/90 shadow-lg flex items-center gap-2"
            >
              <i className="ri-compass-3-line"></i>
              Relancer la visite guidée
            </button>
          </div>
        </div>
      </div>

      {/* ─── Sommaire (chips) ──────────────────────────────── */}
      <div className="sticky top-16 z-30 bg-white/90 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex gap-2 overflow-x-auto no-scrollbar">
          {guideSections.map((s) => (
            <button
              key={s.id}
              onClick={() => {
                setActiveId(s.id);
                document.getElementById(`guide-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className={`px-4 py-2 rounded-full text-xs md:text-sm font-semibold whitespace-nowrap cursor-pointer transition-all flex items-center gap-1.5 ${
                activeId === s.id
                  ? 'bg-clinic-blue text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className={`${s.icon} w-4 h-4 flex items-center justify-center`}></i>
              {s.title}
            </button>
          ))}
        </div>
      </div>

      {/* ─── Contenu ───────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 space-y-8">
        {guideSections.map((section, idx) => (
          <section
            key={section.id}
            id={`guide-${section.id}`}
            className="bg-white rounded-2xl border border-gray-200 overflow-hidden scroll-mt-32"
          >
            {/* En-tête de section */}
            <div className="p-6 md:p-8 border-b border-gray-100 flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center shrink-0">
                <i className={`${section.icon} text-2xl md:text-3xl text-clinic-blue`}></i>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-xs font-bold text-clinic-blue bg-clinic-blue/10 px-2.5 py-1 rounded-full">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <h2 className="text-lg md:text-2xl font-bold text-gray-900">{section.title}</h2>
                </div>
                <p className="text-sm md:text-base text-gray-600">{section.description}</p>
              </div>
            </div>

            {/* Étapes */}
            <div className="p-6 md:p-8 space-y-5">
              {section.steps.map((step, stepIdx) => (
                <div key={stepIdx} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-clinic-blue text-white text-sm font-bold flex items-center justify-center shrink-0">
                      {stepIdx + 1}
                    </div>
                    {stepIdx < section.steps.length - 1 && (
                      <div className="w-0.5 flex-1 bg-clinic-blue/15 my-1"></div>
                    )}
                  </div>
                  <div className="pb-1 flex-1">
                    <h3 className="text-sm md:text-base font-semibold text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.detail}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            {section.actionRoute && section.actionLabel && (
              <div className="px-6 md:px-8 pb-6 md:pb-8">
                <button
                  onClick={() => navigate(section.actionRoute)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold text-sm whitespace-nowrap cursor-pointer"
                >
                  {section.actionLabel}
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            )}
          </section>
        ))}

        {/* ─── Aide supplémentaire ─────────────────────────── */}
        <div className="bg-gradient-to-r from-clinic-blue/10 to-clinic-green/10 rounded-2xl border border-clinic-blue/15 p-6 md:p-8 text-center">
          <i className="ri-question-answer-line text-3xl text-clinic-blue mb-3"></i>
          <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Besoin d'aide supplémentaire ?</h2>
          <p className="text-sm text-gray-600 max-w-xl mx-auto mb-5">
            Si une fonctionnalité vous échappe, relancez la visite guidée ou contactez l'administration
            de la clinique pour être accompagné.
          </p>
          <button
            onClick={replayTour}
            className="inline-flex items-center gap-2 px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-navy transition-all font-semibold text-sm whitespace-nowrap cursor-pointer"
          >
            <i className="ri-compass-3-line"></i>
            Refaire la visite guidée
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}